import React, { memo, useEffect, useState } from 'react';

const E8Summary = ({ 
  controls, 
  currentMaturityLevels, 
  onControlClick,
  answers,
  getQuestionsForML,
  getCompliancePercentage 
}) => {
  const [controlStatuses, setControlStatuses] = useState({});

  // Calculate compliance for each control
  useEffect(() => {
    if (!controls || !currentMaturityLevels.maturityLevel) return;

    const newStatuses = {};
    
    controls.forEach(control => {
      const status = {
        maturityLevel: currentMaturityLevels.maturityLevel
      };

      // Calculate ML1 compliance
      const ml1Questions = getQuestionsForML(control.name, 'ML1');
      const ml1AnsweredQuestions = ml1Questions.filter(q => answers[q.id]?.value);
      const ml1Total = ml1AnsweredQuestions.reduce((sum, q) => {
        return sum + getCompliancePercentage(answers[q.id].value);
      }, 0);
      
      status.ML1 = {
        compliance: ml1Questions.length ? Math.round(ml1Total / ml1Questions.length) : 0
      };

      // Calculate ML2 compliance if applicable
      if (currentMaturityLevels.maturityLevel === 'ML2' || currentMaturityLevels.maturityLevel === 'ML3') {
        if (status.ML1.compliance === 100) {
          const ml2Questions = getQuestionsForML(control.name, 'ML2');
          const ml2AnsweredQuestions = ml2Questions.filter(q => answers[q.id]?.value);
          const ml2Total = ml2AnsweredQuestions.reduce((sum, q) => {
            return sum + getCompliancePercentage(answers[q.id].value);
          }, 0);
          
          status.ML2 = {
            compliance: ml2Questions.length ? Math.round(ml2Total / ml2Questions.length) : 0
          };
        } else {
          status.ML2 = { compliance: 0 };
        }
      }

      // Calculate ML3 compliance if applicable
      if (currentMaturityLevels.maturityLevel === 'ML3') {
        if (status.ML1.compliance === 100 && status.ML2.compliance === 100) {
          const ml3Questions = getQuestionsForML(control.name, 'ML3');
          const ml3AnsweredQuestions = ml3Questions.filter(q => answers[q.id]?.value);
          const ml3Total = ml3AnsweredQuestions.reduce((sum, q) => {
            return sum + getCompliancePercentage(answers[q.id].value);
          }, 0);
          
          status.ML3 = {
            compliance: ml3Questions.length ? Math.round(ml3Total / ml3Questions.length) : 0
          };
        } else {
          status.ML3 = { compliance: 0 };
        }
      }

      newStatuses[control.name] = status;
    });

    setControlStatuses(newStatuses);
  }, [controls, currentMaturityLevels.maturityLevel, answers, getQuestionsForML, getCompliancePercentage]);

  // Get background style based on completion status
  const getBackgroundStyle = (number, status) => {
    const hasProgress = status && Object.values(status).some(level => level?.compliance > 0);
    
    const styles = {
      1: hasProgress ? 'bg-gradient-to-br from-gray-50 to-blue-50 border-l-4 border-blue-400' : 'bg-white border-l-4 border-gray-200',
      2: hasProgress ? 'bg-gradient-to-br from-gray-50 to-indigo-50 border-l-4 border-indigo-400' : 'bg-white border-l-4 border-gray-200',
      3: hasProgress ? 'bg-gradient-to-br from-gray-50 to-purple-50 border-l-4 border-purple-400' : 'bg-white border-l-4 border-gray-200',
      4: hasProgress ? 'bg-gradient-to-br from-gray-50 to-pink-50 border-l-4 border-pink-400' : 'bg-white border-l-4 border-gray-200',
      5: hasProgress ? 'bg-gradient-to-br from-gray-50 to-red-50 border-l-4 border-red-400' : 'bg-white border-l-4 border-gray-200',
      6: hasProgress ? 'bg-gradient-to-br from-gray-50 to-orange-50 border-l-4 border-orange-400' : 'bg-white border-l-4 border-gray-200',
      7: hasProgress ? 'bg-gradient-to-br from-gray-50 to-yellow-50 border-l-4 border-yellow-400' : 'bg-white border-l-4 border-gray-200',
      8: hasProgress ? 'bg-gradient-to-br from-gray-50 to-green-50 border-l-4 border-green-400' : 'bg-white border-l-4 border-gray-200'
    };
    return styles[number] || 'bg-white border-l-4 border-gray-200';
  };

  // Get compliance indicator color
  const getComplianceColor = (compliance) => {
    if (compliance === 100) return 'bg-emerald-400';
    if (compliance >= 75) return 'bg-blue-400';
    if (compliance >= 50) return 'bg-yellow-400';
    if (compliance > 0) return 'bg-orange-400';
    return 'bg-gray-300';
  };

  const getVisibleLevels = () => {
    switch (currentMaturityLevels.maturityLevel) {
      case 'ML3': return ['ML1', 'ML2', 'ML3'];
      case 'ML2': return ['ML1', 'ML2'];
      case 'ML1': return ['ML1'];
      default: return [];
    }
  };

  return (
    <div className="w-full bg-white">
      {/* Always visible controls grid */}
      <div className="grid grid-cols-4 gap-4">
        {controls.map((control, index) => {
          const isActive = currentMaturityLevels.activeTab === index;
          const status = controlStatuses[control.name];
          
          return (
            <div 
              key={control.number}
              className={`
                relative p-4 rounded-lg transition-all duration-200
                ${getBackgroundStyle(control.number, status)}
                ${isActive ? 'ring-2 ring-blue-300 shadow-sm' : ''}
                hover:shadow-md cursor-pointer
              `}
              onClick={() => onControlClick?.(index)}
            >
              <div className="flex items-center gap-2 mb-3">
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-white text-sm font-medium text-gray-700 shadow-sm">
                  {control.number}
                </span>

                <h3 className="font-medium text-gray-700 whitespace-normal break-words min-w-0">
                    {control.name}
                </h3>
                
              </div>

              <div className="space-y-2">
                {getVisibleLevels().map((ml) => {
                  const compliance = status?.[ml]?.compliance || 0;
                  return (
                    <div key={ml} className="flex items-center justify-between bg-white/80 rounded-md px-3 py-2">
                      <span className="text-gray-600 text-sm">{ml}</span>
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${getComplianceColor(compliance)}`} />
                        <span className="text-gray-700 text-sm">{compliance}%</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default memo(E8Summary);